import { Component, Host, h, Prop, Method, Listen, State } from '@stencil/core';
import AuthClient from './services/authClient';
import { hasAuthParams, lt2DaysToExpire } from './utils/utils';
import { dispatch } from './store/store';
@Component({
  tag: 'sy-site-auth',
  shadow: false,
})
export class SySiteAuth {
  private loginComponentEl: any;

  private email: string;

  private isReadyResolve = null;
  private isReady = new Promise(resolve => {
    this.isReadyResolve = resolve;
  });

  /**
   * Resolves when auth is ready, i.e. logged-in user is available
   */
  @Method()
  whenReady(): Promise<any> {
    return this.isReady;
  }

  @State() renderLogin = false;

  /**
   * Login component
   */
  @Prop() component: string;

  /**
   * Login component properties
   */
  @Prop() loginComponentProps: { [prop: string]: any } = {};

  private removeAcessTokenFromUrl = () => {
    const url = new URL(window.location.href);
    const queryParams = new URLSearchParams(url.search);

    if (queryParams.has('accessToken')) {
      queryParams.delete('accessToken');
    }

    return url.pathname + queryParams.toString();
  };

  private handleResponse(response: Response) {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  }

  @Listen('sySiteLoginDidSubmit')
  handleLogin(event: CustomEvent): void {
    const obj_url = new URL(window.location.href);
    const queryParams = new URLSearchParams(obj_url.search);
    const redirect = queryParams.get('redirect') || window.location.pathname;
    if (event.detail.type === 'social') {
      AuthClient.loginWithRedirect({ authenticator: event.detail.data, redirect });
    } else if (event.detail.type === 'email') {
      this.email = event.detail.data.email;
      AuthClient.initEmailAuth(event.detail.data)
        .then(this.handleResponse)
        .then(() => {
          this.loginComponentEl.changeEmailPhase('token');
        })
        .catch(error => {
          error.json().then(messages => {
            let errorMessage = '';
            messages._messages.forEach(message => (errorMessage += `${message.text} \n`));
            this.loginComponentEl.setError(errorMessage);
            this.loginComponentEl.changeEmailPhase('error');
          });
        });
    } else if (event.detail.type === 'token') {
      AuthClient.emailAuth({ email: this.email, code: event.detail.data, redirect });
    }
  }

  /**
   * Logout user
   */
  @Method()
  async logout(): Promise<void> {
    AuthClient.logout();
    dispatch({ type: 'LOGOUT' });
  }

  /**
   * Logs the user in
   */
  @Method()
  async login(): Promise<void> {
    this.loginComponentEl.open();
  }

  componentWillRender() {
    const fn = async () => {
      dispatch({ type: 'INITIALISED' });

      try {
        if (hasAuthParams()) {
          await AuthClient.login();
          history.replaceState(
            {
              id: 'No token',
              source: 'web',
            },
            'No token',
            this.removeAcessTokenFromUrl(),
          );
          dispatch({ type: 'COMPLETE', user: AuthClient.getUser() });
        } else {
          if (AuthClient.isAuthenticated()) {
            const user = AuthClient.getUser();
            if (user && lt2DaysToExpire(user.exp)) {
              await AuthClient.renewToken(user.token);
              dispatch({ type: 'COMPLETE', user });
            } else {
              dispatch({ type: 'COMPLETE', user });
            }
          } else {
            dispatch({ type: 'COMPLETE', user: null });
          }
        }
        this.isReadyResolve();
      } catch (error) {
        dispatch({ type: 'ERROR', error: error });
      }
    };
    fn();
  }

  render() {
    const LoginComponent = this.component;

    return (
      <Host>
        <LoginComponent ref={el => (this.loginComponentEl = el)} {...this.loginComponentProps}></LoginComponent>
        <slot></slot>
      </Host>
    );
  }
}
